import {
  collection,
  addDoc,
  doc,
  updateDoc,
  query,
  where,
  getCountFromServer,
  getDocs,
  writeBatch,
  Timestamp,

} from "firebase/firestore";
import { createQuery, db } from "../providers/database";
import { JobDetailsNewCollection } from "../dataTypes/JobDetailsNewCollection";

import { collectionNames } from "./dictionary/collectionNames";
import { useQuery } from "react-query";

const jobDetailsNewCollectionRef = collection(
  db,
  collectionNames.jobDetailsNewCollection,
);

// Create a new entry for JobDetailsNewCollection
export async function createNewEntryForJobDetailsNewCollection(
  jobDetailsNew: JobDetailsNewCollection,
) {
  const newJob = await addDoc(jobDetailsNewCollectionRef, {
    ...jobDetailsNew,
  });
  return newJob.id;
}

// Update entry for JobDetailsNewCollection
export async function updateEntryForJobDetailsNewCollection(id: string, jobDetailsNew: any) {

  // Remove undefined values before updating Firestore
  const cleanData: any = Object.fromEntries(
    Object.entries(jobDetailsNew).filter(([_, v]) => v !== undefined)
  );

  try {
    const updatedJob = await updateDoc(
      doc(db, collectionNames.jobDetailsNewCollection, id),
      cleanData
    );
    return updatedJob;
  } catch (error) {
    console.error("Firestore Update Error:", error);
    throw error;
  }
}





// Update company logo for all JobDetailsNew documents belonging to employerId
export async function updateLogoForEmployerJobs(employerId?: string, logoUrl?: string) {


  try {
    // Step 1: Query all job records for this employer
    const jobRef = collection(db, collectionNames.jobDetailsNewCollection);

    const q = query(jobRef, where("companyId", "==", employerId));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.warn("No job records found for employer:", employerId);
      return { updated: 0 };
    }

    // Step 2: Use Firestore batch update
    const batch = writeBatch(db);
    let updateCount = 0;

    snapshot.forEach((docSnap) => {
      const ref = doc(db, collectionNames.jobDetailsNewCollection, docSnap.id);

      batch.update(ref, {
        companyLogo: logoUrl, // only updating logo
      });

      updateCount++;
    });

    // Step 3: Commit batch
    await batch.commit();

    console.log(`Updated ${updateCount} job records with new logo.`);
    return { updated: updateCount };

  } catch (error) {
    console.error("Error updating company logos:", error);
    throw error;
  }
}



export function useJobDetailsNewCollection(jobId: string) {
  return useQuery(
    ["JobDetailsNewCollection", { jobId }],
    createQuery(() => doc(db, collectionNames.jobDetailsNewCollection, jobId)),
    { enabled: !!jobId },
  );
}

export function getIsSubmittingJobFirstTime(jobData: any) {
  return (
    jobData?.solutionType === null ||
    jobData?.startDate === null ||
    jobData?.openings === null ||
    jobData?.applicationFlow === null ||
    jobData?.workSettings === null ||
    jobData?.workType === null ||
    jobData?.workTerm === null ||
    jobData?.hoursPerWeek === null ||
    jobData?.payRate === null ||
    jobData?.jobDescription === null
  );
}

export function useAllEmployerJobsByEmployerIdFromJobDetailsNewCollection(
  companyId: string,
) {
  return useQuery(
    ["AllEmployerJobsByEmployerIdFromJobDetailsNewCollection", { companyId }],
    createQuery(() =>
      query(
        collection(db, collectionNames.jobDetailsNewCollection),
        where("jobState", "==", "live"),
        where("companyId", "==", companyId),
      ),
    ),
    { enabled: !!companyId },
  );
}

export function useLiveJobsCountByDays(days: 3 | 7 | 14 | 30 | 90 | 365) {
  return useQuery(
    ["LiveJobsCountByDays", { days }],
    async () => {
      if (!days) return 0;

      const jobsRef = collection(db, collectionNames.jobDetailsNewCollection);
      const now = new Date();
      let q;

      if (days === 365) {
        // Last Full Year (Jan 1 to Dec 31 of last year)
        const lastYear = now.getFullYear() - 1;
        const startTimestamp = Math.floor(Date.UTC(lastYear, 0, 1) / 1000);
        const endTimestamp = Math.floor(
          Date.UTC(lastYear, 11, 31, 23, 59, 59) / 1000
        );

        q = query(
          jobsRef,
          where(
            "makeLiveDate",
            ">=",
            Timestamp.fromMillis(startTimestamp * 1000)
          ),
          where("makeLiveDate", "<=", Timestamp.fromMillis(endTimestamp * 1000))
        );
      } else if (days === 90) {
        // Last Full Quarter
        const currentQuarter = Math.floor(now.getMonth() / 3);
        let targetQ, targetY;
        if (currentQuarter === 0) {
          targetQ = 3;
          targetY = now.getFullYear() - 1;
        } else {
          targetQ = currentQuarter - 1;
          targetY = now.getFullYear();
        }
        const startMonth = targetQ * 3;
        const startTimestamp = Math.floor(
          Date.UTC(targetY, startMonth, 1) / 1000
        );
        const endTimestamp = Math.floor(
          Date.UTC(targetY, startMonth + 3, 0, 23, 59, 59) / 1000
        );

        q = query(
          jobsRef,
          where(
            "makeLiveDate",
            ">=",
            Timestamp.fromMillis(startTimestamp * 1000)
          ),
          where("makeLiveDate", "<=", Timestamp.fromMillis(endTimestamp * 1000))
        );
      } else {
        // Standard N days logic (Rolling window)
        const startTimestamp =
          Math.floor(now.getTime() / 1000) - days * 24 * 60 * 60;

        q = query(
          jobsRef,
          where(
            "makeLiveDate",
            ">=",
            Timestamp.fromMillis(startTimestamp * 1000)
          )
        );
      }

      const snapshot = await getCountFromServer(q);
      return snapshot.data().count;
    },
    {
      enabled: !!days,
      staleTime: 5 * 60 * 1000, // 5 minutes cache
    }
  );
}


export function useCountOfActiveJobs() {
  return useQuery(
    ["AllActiveJobsStats"],
    async () => {
      const jobsRef = collection(
        db,
        collectionNames.jobDetailsNewCollection
      );

      const liveQuery = query(jobsRef, where("jobState", "==", "live"));
      const bpoQuery = query(
        jobsRef,
        where("jobState", "==", "live"),
        where("solutionType", "==", "bpo")
      );
      const jobBoardQuery = query(
        jobsRef,
        where("jobState", "==", "live"),
        where("solutionType", "==", "job_board")
      );

      const [liveSnap, bpoSnap, jobBoardSnap] = await Promise.all([
        getCountFromServer(liveQuery),
        getCountFromServer(bpoQuery),
        getCountFromServer(jobBoardQuery),
      ]);

      return {
        totalLive: liveSnap.data().count,
        totalBPO: bpoSnap.data().count,
        totalJobBoard: jobBoardSnap.data().count,
      };
    },
    {
      // Always run once, since no company filter is applied
      enabled: true,
    }
  );
}









export function useAllEmployerJobsWithCreditUsedByEmployerIdFromJobDetailsNewCollection(
  companyId: string,
) {
  return useQuery(
    [
      "AllEmployerLiveAndClosedJobsByEmployerIdFromJobDetailsNewCollection",
      { companyId },
    ],
    createQuery(() =>
      query(
        collection(db, collectionNames.jobDetailsNewCollection),
        where("companyId", "==", companyId),
      ),
    ),
    { enabled: !!companyId },
  );
}




// Accept plain timestamp-like objects and convert them
export const getJobsByMakeLiveDateRange = async (
  startTimestampObj: { seconds: number; nanoseconds: number },
  endTimestampObj: { seconds: number; nanoseconds: number }
) => {
  console.log("Function triggered with:", startTimestampObj, endTimestampObj);

  try {
    const startTimestamp = new Timestamp(
      startTimestampObj.seconds,
      startTimestampObj.nanoseconds
    );
    const endTimestamp = new Timestamp(
      endTimestampObj.seconds,
      endTimestampObj.nanoseconds
    );

    const jobRef = collection(db, "JobDetailsNewCollection");
    const q = query(
      jobRef,
    );

    const snapshot = await getDocs(q);

    const jobs = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));


    return jobs;
  } catch (err) {
    console.error("Error fetching jobs by date range:", err);
    throw err;
  }
};
